import React from 'react';
import styled, { ThemeProvider } from 'styled-components';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import AffiliateTheme from '../../styles/AffiliateTheme';
import { motion } from 'framer-motion';
import LanguageSwitcher from './LanguageSwitcher';
import MobileMenu from './MobileMenu';

// Composant de mise en page pour les pages d'affiliation
const AffiliateLayout = ({ children, showHeader = true, showFooter = true }) => {
  const { t } = useTranslation();
  
  return (
    <ThemeProvider theme={AffiliateTheme}>
      <LayoutContainer>
        {showHeader && (
          <Header
            as={motion.header}
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <HeaderContent>
              <LogoContainer to="/">
                <Logo src="/assets/icon.png" alt="Xenya" />
                <LogoText>Xenya <span>Affiliate</span></LogoText>
              </LogoContainer>
              
              <Navigation>
                <NavLink to="/affiliate">
                  {t('navigation.affiliate.home')}
                </NavLink>
                <NavLink to="/affiliate/dashboard">
                  {t('navigation.affiliate.dashboard')}
                </NavLink>
                <NavLink to="/">
                  {t('navigation.affiliate.mainSite')}
                </NavLink>
                <LangSwitcherWrapper>
                  <LanguageSwitcher />
                </LangSwitcherWrapper>
              </Navigation>
              
              <MobileMenuWrapper>
                <MobileMenu />
              </MobileMenuWrapper>
            </HeaderContent>
          </Header>
        )}
        
        <MainContent>
          {children}
        </MainContent>
        
        {showFooter && (
          <Footer>
            <FooterContent>
              <FooterLinks>
                <FooterLink to="/affiliate/terms">
                  {t('navigation.affiliate.terms')}
                </FooterLink>
                <FooterLink to="/privacy">
                  {t('navigation.privacy')}
                </FooterLink>
                <FooterLink to="/support">
                  {t('navigation.support')}
                </FooterLink>
              </FooterLinks>
              
              <FooterCopyright>
                &copy; {new Date().getFullYear()} Xenya. {t('footer.allRightsReserved')}
              </FooterCopyright>
            </FooterContent>
          </Footer>
        )}
      </LayoutContainer>
    </ThemeProvider>
  );
};

// Styled Components
const LayoutContainer = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
`;

const Header = styled.header`
  background: ${({ theme }) => theme.affiliate.gradients.dark};
  color: ${({ theme }) => theme.affiliate.colors.text.contrast.dark};
  padding: 15px 0;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
`;

const HeaderContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const LogoContainer = styled(Link)`
  display: flex;
  align-items: center;
  text-decoration: none;
  color: ${({ theme }) => theme.affiliate.colors.text.contrast.dark};
  transition: opacity ${({ theme }) => theme.affiliate.animationDuration};
  
  &:hover {
    opacity: 0.9;
  }
`;

const Logo = styled.img`
  width: 32px;
  height: 32px;
  margin-right: 10px;
`;

const LogoText = styled.h1`
  font-size: 1.5rem;
  font-weight: 600;
  margin: 0;
  
  span {
    font-weight: 400;
    opacity: 0.8;
  }
`;

const Navigation = styled.nav`
  display: flex;
  gap: 20px;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    display: none;
  }
`;

const NavLink = styled(Link)`
  color: ${({ theme }) => theme.affiliate.colors.text.contrast.dark};
  text-decoration: none;
  padding: 5px 0;
  position: relative;
  font-weight: 500;
  
  &:after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 0;
    height: 2px;
    background-color: ${({ theme }) => theme.affiliate.colors.accent};
    transition: width ${({ theme }) => theme.affiliate.animationDuration};
  }
  
  &:hover {
    color: ${({ theme }) => theme.affiliate.colors.accent};
  }
  
  &:hover:after {
    width: 100%;
  }
`;

const MainContent = styled.main`
  flex: 1;
  background-color: ${({ theme }) => theme.affiliate.colors.background};
`;

const Footer = styled.footer`
  background: ${({ theme }) => theme.affiliate.gradients.dark};
  color: ${({ theme }) => theme.affiliate.colors.text.contrast.dark};
  padding: 30px 0;
  box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.1);
`;

const FooterContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 15px;
`;

const FooterLinks = styled.div`
  display: flex;
  gap: 20px;
`;

const FooterLink = styled(Link)`
  color: ${({ theme }) => theme.affiliate.colors.text.contrast.dark};
  text-decoration: none;
  opacity: 0.8;
  transition: all ${({ theme }) => theme.affiliate.animationDuration};
  
  &:hover {
    opacity: 1;
    color: ${({ theme }) => theme.affiliate.colors.accent};
  }
`;

const FooterCopyright = styled.div`
  font-size: 0.9rem;
  opacity: 0.6;
`;

const MobileMenuWrapper = styled.div`
  display: none;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    display: block;
  }
`;

const LangSwitcherWrapper = styled.div`
  margin-left: 20px;
`;

export default AffiliateLayout;